import isBrowser from "utils/misc/isBrowser";
import {CookiesConstants, LocalStorageConstants} from "utils/constants";
import { UAParser } from "ua-parser-js";
import { parseCookies, setCookie } from "utils/nookies";
import { jsonSafeGet } from "utils/Helpers";
import {
  getLocalStorageAuthUser,
  LiteUserInfo,
} from "dataLayer/localStorage/localStorageMember";
import {
  EnumLoggingScreenNames,
  IGetDeviceDetails,
  IGetInformationFromCookies,
  INetworkConnection,
  IUtms,
} from "../types/loggingTypes";
import { DEFAULT_VALUE } from "./sendLoggingEvent";

export class CustomDimensionsClass {
  private static instance;
  private static cookies;
  private static auth;

  public currentScreen: EnumLoggingScreenNames;
  public sessionFunnelSource: EnumLoggingScreenNames;

  public static getInstance(): CustomDimensionsClass {
    if (!CustomDimensionsClass.instance) {
      CustomDimensionsClass.instance = new CustomDimensionsClass();
      CustomDimensionsClass.auth = getLocalStorageAuthUser();
    }
    CustomDimensionsClass.cookies = parseCookies();
    return CustomDimensionsClass.instance;
  }

  setPreviousWidget = (currentWidget) => {
    currentWidget
      ? setCookie(null, CookiesConstants.PREVIOUS_LOGGING_WIDGET, currentWidget)
      : DEFAULT_VALUE;
  };
  checkCurrentScreen = ()=>{
    let prev = CustomDimensionsClass.cookies[CookiesConstants.PREVIOUS_LOGGING_SCREEN_NAME] ?
        CustomDimensionsClass.cookies[CookiesConstants.PREVIOUS_LOGGING_SCREEN_NAME].split(",") : [EnumLoggingScreenNames.NA];
    if (prev[prev.length-1] != localStorage.getItem(LocalStorageConstants.CURRENT_LOGGING_SCREEN_NAME)) {
      prev = [prev[prev.length-1] , localStorage.getItem(LocalStorageConstants.CURRENT_LOGGING_SCREEN_NAME)];
      setCookie(null, CookiesConstants.PREVIOUS_LOGGING_SCREEN_NAME,prev.join(","));
    }
  }
  getCurrentScreen = ()=>{
    return localStorage.getItem(LocalStorageConstants.CURRENT_LOGGING_SCREEN_NAME)
  }
  setPreviousActions = (currentActions) => {
    setCookie(
      null,
      CookiesConstants.PREVIOUS_LOGGING_ACTIONS,
      JSON.stringify(currentActions)
    );
  };

  getPreviousWidget = (): string => {
    const previousWidgetName =
      CustomDimensionsClass.cookies[CookiesConstants.PREVIOUS_LOGGING_WIDGET];
    return !previousWidgetName ? "NA" : (previousWidgetName as string);
  };

  getPreviousActions = () => {
    let previousActions = jsonSafeGet<any>(
      CustomDimensionsClass.cookies[CookiesConstants.PREVIOUS_LOGGING_ACTIONS],
      {}
    );
    if (previousActions) {
      const { L1, L2, L3 } = previousActions;
      return { L1, L2, L3 };
    }
    return { L1: "NA", L2: "NA", L3: "NA" };
  };

  getDeviceDetails(): IGetDeviceDetails {
    let ua = isBrowser && new UAParser(navigator.userAgent);
    const browserName = ua.getBrowser().name || DEFAULT_VALUE;
    const browserVersion = ua.getBrowser().version || DEFAULT_VALUE;
    const deviceBrand = ua.getDevice().vendor || DEFAULT_VALUE;
    const deviceModel = ua.getDevice().model || DEFAULT_VALUE;
    const deviceCpuArch = ua.getCPU().architecture || DEFAULT_VALUE;
    const deviceLanguage =
      (isBrowser && navigator.language?.split("-")?.[0]) ?? DEFAULT_VALUE;
    const deviceOS = ua.getOS().name || DEFAULT_VALUE;
    const deviceOSVersion = ua.getOS().version || DEFAULT_VALUE;
    const now = new Date();
    const timeZoneOffset = now.getTimezoneOffset();
    const hoursOffset = Math.abs(Math.floor(timeZoneOffset / 60));
    const minutesOffset = Math.abs(timeZoneOffset % 60);
    const sign = timeZoneOffset >= 0 ? "-" : "+";
    const deviceTimeZone =
      `UTC${sign}${hoursOffset.toString().padStart(2, "0")}:${minutesOffset
        .toString()
        .padStart(2, "0")}` || DEFAULT_VALUE;
    return {
      browserName,
      browserVersion,
      deviceBrand,
      deviceModel,
      deviceCpuArch,
      deviceLanguage,
      deviceOS,
      deviceOSVersion,
      deviceTimeZone,
    };
  }

  getConnectionInfo() {
    if (isBrowser && !navigator?.connection){
      return { connectionType:DEFAULT_VALUE, connectionLatency:DEFAULT_VALUE}
    }
    const { effectiveType } =
      isBrowser && (navigator?.connection as INetworkConnection);
    const { rtt } = isBrowser && (navigator?.connection as INetworkConnection);
    const connectionType = effectiveType || DEFAULT_VALUE;
    const connectionLatency = rtt || DEFAULT_VALUE;
    return { connectionType, connectionLatency };
  }

  getHttpReferrer(): string {
    const httpReferrer = (isBrowser && document.referrer) || DEFAULT_VALUE;
    return httpReferrer;
  }

  getDeviceUserAgent(): string {
    return (isBrowser && navigator?.userAgent) || DEFAULT_VALUE;
  }

  getScreenResolution(): string {
    return (
      (isBrowser && `${window.screen.width}x${window.screen.height}`) ||
      DEFAULT_VALUE
    );
  }

  getSessionDetails(): IGetInformationFromCookies {
    const cookies = CustomDimensionsClass.cookies;
    const source =
      cookies["source"] === "desktop" ? "web_desktop" : "web_mobile";
    const sessionId = jsonSafeGet<any>(cookies[CookiesConstants.SESSION], {})?.id || DEFAULT_VALUE;
    const sessionStarted = jsonSafeGet<any>(
        cookies[CookiesConstants.SESSION],
        {}
    )?.startedAt;
    const trackingId = cookies[CookiesConstants.DEVICE_UUID] ?? "NA";
    const userInfo = jsonSafeGet<LiteUserInfo>(
      cookies[CookiesConstants.USER_INFO]
    );
    let currentTime = new Date().getTime();
    let sessionLife = Math.floor((currentTime - sessionStarted) / 1000)  || !sessionStarted && 0;
    if(sessionLife < 0 ){
      sessionLife = 1
    }
    const postCountry = cookies[CookiesConstants.COUNTRY] ?? "xx";
    return {
      source,
      sessionId,
      trackingId,
      userInfo,
      sessionLife,
      postCountry,
    };
  }

  getUtms(): IUtms {
    const query = new URLSearchParams(window.location.search);
    const utmMedium = query?.get("utm_medium")
      ? query?.get("utm_medium")
      : DEFAULT_VALUE;
    const utmCampaign = query?.get("utm_campaign")
      ? query?.get("utm_campaign")
      : DEFAULT_VALUE;
    const utmSource = query?.get("utm_source")
      ? query?.get("utm_source")
      : DEFAULT_VALUE;
    return { utmMedium, utmCampaign, utmSource };
  }

  getUserInfo() {
    // @todo replace with sso get idintity
    const { userInfo } = this.getSessionDetails();
    const userId = userInfo.id || DEFAULT_VALUE;
    const userAuthentication =
      userId !== DEFAULT_VALUE ? "LoggedIn" : "LoggedOut";
    return { userId, userAuthentication };
  }
  setSessionFunnelSource(screen: EnumLoggingScreenNames) {
    if (screen) {
      this.sessionFunnelSource = screen;
      return
    } else this.sessionFunnelSource = EnumLoggingScreenNames.NA;
  }
  getSessionFunnelSource() {
    return this.sessionFunnelSource;
  }
}
